import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BaseRequest, BaseResponse } from '../models'

import {
  FileModel,
  FileUpdateRequest,
  FileUploadRequest,
  FileUploadRequestResponse,
  SessionImportAnonymousUser,
  SessionImportManagedUser,
  SessionImportUser,
} from '@zocks-communications/client-sdk-js'
import { FileDownloadLocation, FileKind } from '../../models/File'
import { AiFeature } from '../../models/AiFeature'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { Allocation, AllocationContextUser, UserAllocation, UserAllocationContextUser, UserAllocationUpdate } from '../../models/Allocation'
import { Room, RoomSession, RoomSessionConfig, RoomSessionLanguageConfig } from '../../models/Room'
import { AiResult } from '../../models/AiResult'
import { faApi } from '../../apps/professional-app/fa/store/fa'
import { ManualParticipant, ManualParticipantUpsert } from '../../models/ManualParticipant'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_API_BASE_URL!

export interface BaseSessionRequest extends BaseRequest {
  roomId: string
  sessionId: string
}

export interface UpdateSessionConfigBody {
  autoJoin?: boolean | null
  language?: RoomSessionLanguageConfig
}

export interface UpdateSessionConfigRequest extends BaseSessionRequest {
  body: UpdateSessionConfigBody
}

export interface JoinAdhocMeetingBody {
  url: string
}

export interface JoinAdhocMeetingRequest extends BaseRequest {
  body: JoinAdhocMeetingBody
}

export interface SessionFilterRequest extends BasePageRequest {
  userId?: string
  isActive?: boolean
  kind?: 'external' | 'internal'
  startedAfter?: number
}

export enum RoomSessionKind {
  IN_PERSON = 'inPerson',
  UPCOMING_IN_PERSON = 'upcomingInPerson',
  VIRTUAL_PARTICIPANT = 'virtualParticipant',
  IMPORT = 'import',
}

export interface BaseStartSessionBody {
  kind: RoomSessionKind
  startedAt?: string
  users?: Array<SessionImportAnonymousUser | SessionImportManagedUser>
  metadata?: Record<string, unknown>
  roomId?: string
  sessionId?: string
}

export interface InPersonStartSessionBody extends BaseStartSessionBody {
  kind: RoomSessionKind.IN_PERSON
}

export interface UpcomingInPersonStartSessionBody extends BaseStartSessionBody {
  kind: RoomSessionKind.UPCOMING_IN_PERSON
  finishedAt?: string
}

export interface VirtualParticipantStartSessionBody extends BaseStartSessionBody {
  kind: RoomSessionKind.VIRTUAL_PARTICIPANT
  url?: string
}

export interface ImportStartSessionBody extends BaseStartSessionBody {
  kind: RoomSessionKind.IMPORT
  fileId: string
  finishedAt?: string
}

export interface StartSessionRequest extends BaseRequest {
  body: InPersonStartSessionBody | VirtualParticipantStartSessionBody | ImportStartSessionBody | UpcomingInPersonStartSessionBody
}

export interface FinishSessionBody {
  roomId: string
  finishedAt?: string
}

export interface FinishSessionRequest extends BaseRequest {
  sessionId: string
  body: FinishSessionBody
}

export interface ListFilesRequest extends BasePageRequest {
  roomId: string
  sessionId: string
  kind?: FileKind
}

export interface UploadSessionFileRequest extends BaseRequest {
  roomId: string
  sessionId: string
  body: FileUploadRequest
}

export interface UpdateFileRequest extends BaseRequest {
  fileId: string
  body: FileUpdateRequest
}

export interface RoomRequest extends BaseRequest {
  roomId: string
}

export interface RoomSessionRequest extends BaseRequest {
  roomId: string
  sessionId: string
}

export interface RoomUpdateConfig {
  autoJoin: boolean | null
}

export interface RoomUpdateRequestBody {
  config: RoomUpdateConfig
}

export interface RoomUpdateRequest extends RoomRequest {
  body: RoomUpdateRequestBody
}

export interface UpdateUserAllocationRequest extends BaseSessionRequest {
  body: UserAllocationUpdate
}

export interface GetAllocationContextUsersRequest extends BaseSessionRequest {
  identityTypes?: string[]
}

export interface AddAllocationContextUsersRequest extends BaseSessionRequest {
  body: Omit<AllocationContextUser, 'kind'>[]
}

export interface RemoveAllocationContextUserRequest extends BaseSessionRequest {
  body: { userId: string }
}

export interface TriggerEventBody {
  event: string
}

export interface TriggerEvent extends BaseSessionRequest {
  body: TriggerEventBody
}

export interface ListAiResultsRequest extends BasePageRequest {
  roomId: string
  sessionId: string
}

export interface ListAIFeaturesRequest extends BasePageRequest {
  roomId: string
  sessionId: string
  includeRestricted?: boolean
}

export interface ListRoomEventsRequest extends BasePageRequest {
  roomId: string
  sessionId: string
  types: string[]
}

export interface RoomEvent {
  id: string
  sessionId: string
  type: string
  participantId?: string
  timestamp: string
  // Shape is event-type-dependent and consumed via dynamic property access; see callers in hooks.ts / executedAiFeatures.ts.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: { [key: string]: any }
}

export interface AIFeatureInfo {
  id: string
  presetId?: string
  inputParametersHierarchyId?: string
}

export interface UpdateSessionMetadataRequest extends BaseSessionRequest {
  metadata: Record<string, unknown>
}

export interface ChangeSourceRequest extends BaseSessionRequest {
  source: 'voice-memo' | 'phone'
  provider?: 'ringcentral'
}

export interface ChangePrivacyRequest extends BaseSessionRequest {
  isPrivate: boolean
}

export interface TranscriptionTrimmingRequest extends BaseRequest {
  start: number
  duration: number
  roomId: string
  roomSessionId: string
}

export interface OverrideMeetingConsentRequest extends BaseSessionRequest {
  reason?: string
}

export interface ImportSessionRequest extends BaseRequest {
  body: ImportSession
}

export interface ImportSession {
  roomId?: string
  startedAt: string
  finishedAt: string
  fileId: string
  users: SessionImportUser[]
  metadata?: object
  config?: RoomSessionConfig
}

export interface UpsertManualParticipantRequest extends BaseSessionRequest {
  body: ManualParticipantUpsert
}

export interface GetManualParticipantsRequest extends BaseSessionRequest {
  excludeWithAllocation?: boolean
}

export interface DeleteManualParticipantRequest extends BaseSessionRequest {
  userId: string
}

export interface RoomIdSessionId {
  roomId: string
  sessionId: string
}

export interface GetSessionsByIdsRequest extends BaseRequest {
  body: {
    roomIdSessionIds: RoomIdSessionId[]
  }
}

export const mediaApi = createApi({
  reducerPath: 'mediaApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['UserActiveSessions', 'RoomById', 'RoomSessionById', 'UserAllocation', 'UserAllocationContextUsers', 'ManualParticipants'],
  endpoints: (builder) => ({
    updateSessionConfig: builder.mutation<BaseResponse<RoomSession>, UpdateSessionConfigRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/update-config`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    updateSessionMetadata: builder.mutation<BaseResponse<RoomSession>, UpdateSessionMetadataRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/update-metadata/`,
          body: request.metadata,
          method: 'POST',
        }
      },
      invalidatesTags: ['RoomSessionById'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(faApi.util.invalidateTags(['Session']))
      },
    }),
    changeSource: builder.mutation<void, ChangeSourceRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/change-source`,
          params: {
            source: request.source,
            ...(request.provider ? { provider: request.provider } : {}),
          },
          method: 'POST',
        }
      },
    }),
    changePrivacy: builder.mutation<void, ChangePrivacyRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/change-privacy`,
          params: { isPrivate: request.isPrivate },
          method: 'POST',
        }
      },
    }),
    joinAdhocMeeting: builder.mutation<void, JoinAdhocMeetingRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/adhoc-meetings`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    getUserActiveSessions: builder.query<BaseResponse<RoomSession[]>, SessionFilterRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/sessions`,
          params: {
            userId: request.userId,
            isActive: true,
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            kind: request.kind,
            startedAfter: request.startedAfter,
          },
        }
      },
      providesTags: ['UserActiveSessions'],
    }),
    getRoom: builder.query<Room, RoomRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/`,
        }
      },
      transformResponse(response: BaseResponse<Room>) {
        return response.data
      },
      providesTags: ['RoomById'],
    }),
    updateRoom: builder.mutation<void, RoomUpdateRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}`,
          body: request.body,
          method: 'PATCH',
        }
      },
      invalidatesTags: ['RoomSessionById', 'RoomById'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(faApi.util.invalidateTags(['Session']))
      },
    }),
    getRoomSession: builder.query<BaseResponse<RoomSession>, RoomSessionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      providesTags: ['RoomSessionById'],
    }),
    startSession: builder.mutation<RoomSession, StartSessionRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/sessions/start`,
          body: request.body,
          method: 'POST',
        }
      },
      transformResponse: (response: { data: RoomSession }) => response.data,
    }),
    finishSession: builder.mutation<void, FinishSessionRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/sessions/${request.sessionId}/finish`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    listFiles: builder.query<BaseResponse<FileModel[]>, ListFilesRequest>({
      query: (request) => {
        const {
          accountId,
          roomId,
          sessionId,
          kind,
          pageRequest: { page, size },
        } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/files/`,
          params: {
            page,
            size,
            kind,
          },
        }
      },
    }),
    uploadSessionFileRequest: builder.mutation<FileUploadRequestResponse, UploadSessionFileRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/files/upload`,
          body: request.body,
          method: 'POST',
        }
      },
      transformResponse: (response: { data: FileUploadRequestResponse }) => response.data,
    }),
    updateFile: builder.mutation<FileModel, UpdateFileRequest>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/files/${request.fileId}`,
          body: request.body,
          method: 'PATCH',
        }
      },
      transformResponse: (response: { data: FileModel }) => response.data,
    }),
    getUserAllocation: builder.query<Allocation[], RoomSessionRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation`,
        }
      },
      transformResponse: (response: BaseResponse<UserAllocation>) => {
        return response.data.allocations
      },
      providesTags: ['UserAllocation'],
    }),
    updateUserAllocation: builder.mutation<void, UpdateUserAllocationRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation`,
          body: request.body,
          method: 'PUT',
        }
      },
      invalidatesTags: ['UserAllocation', 'RoomSessionById'],
    }),
    getUserAllocationContextUsers: builder.query<BaseResponse<UserAllocationContextUser[]>, GetAllocationContextUsersRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/context-users`,
          params: request.identityTypes ? { identityTypes: request.identityTypes.join(',') } : {},
        }
      },
      providesTags: ['UserAllocationContextUsers'],
    }),
    upsertManualParticipant: builder.mutation<void, UpsertManualParticipantRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/manual-participants`,
        method: 'PUT',
        body: request.body,
      }),
      invalidatesTags: ['RoomSessionById', 'ManualParticipants'],
    }),
    getManualParticipants: builder.query<BaseResponse<ManualParticipant[]>, GetManualParticipantsRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/manual-participants`,
          params: request.excludeWithAllocation == null ? undefined : { excludeWithAllocation: request.excludeWithAllocation },
        }
      },
      providesTags: ['ManualParticipants'],
    }),
    deleteManualParticipant: builder.mutation<void, DeleteManualParticipantRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/manual-participants/${request.userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['RoomSessionById', 'ManualParticipants'],
    }),
    addAllocationContextUsers: builder.mutation<void, AddAllocationContextUsersRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/add-context-users`,
          body: request.body,
          method: 'POST',
        }
      },
      invalidatesTags: ['UserAllocationContextUsers'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(faApi.util.invalidateTags(['Session']))
      },
    }),
    removeAllocationContextUser: builder.mutation<void, RemoveAllocationContextUserRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/user-allocation/remove-context-user`,
          body: request.body,
          method: 'POST',
        }
      },
      invalidatesTags: ['UserAllocationContextUsers'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        await queryFulfilled
        dispatch(faApi.util.invalidateTags(['Session']))
      },
    }),
    triggerEvent: builder.mutation<void, TriggerEvent>({
      query: (request) => {
        return {
          url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/trigger-event`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    listAiResults: builder.query<BaseResponse<AiResult[]>, ListAiResultsRequest>({
      query: (request) => {
        const {
          accountId,
          roomId,
          sessionId,
          pageRequest: { page, size },
        } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/ai-feature-results`,
          params: {
            page,
            size,
          },
        }
      },
    }),
    listAIFeatures: builder.query<BaseResponse<AiFeature[]>, ListAIFeaturesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/ai-features`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            includeRestricted: request.includeRestricted,
          },
        }
      },
    }),
    listRoomEvents: builder.query<BaseResponse<RoomEvent[]>, ListRoomEventsRequest>({
      query: (request) => {
        const {
          accountId,
          roomId,
          sessionId,
          pageRequest: { page, size },
          types,
        } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/events`,
          params: {
            page,
            size,
            types: types.join(),
            session_id: sessionId,
          },
        }
      },
    }),
    deleteSession: builder.mutation<null, BaseSessionRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: ['RoomSessionById'],
    }),
    endSession: builder.mutation<null, BaseSessionRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId } = request
        return {
          url: `/accounts/${accountId}/sessions/${sessionId}/end`,
          body: { roomId },
          method: 'POST',
        }
      },
    }),
    getTranscriptionFileLocation: builder.query<FileDownloadLocation, RoomSessionRequest>({
      query: (request) => {
        const { accountId, roomId, sessionId } = request
        return {
          url: `/accounts/${accountId}/rooms/${roomId}/sessions/${sessionId}/files/download/transcription`,
        }
      },
      transformResponse: (response: BaseResponse<FileDownloadLocation>) => {
        return response.data
      },
    }),
    trimMeeting: builder.mutation<void, TranscriptionTrimmingRequest>({
      query: (request) => ({
        url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.roomSessionId}/trim`,
        method: 'POST',
        body: {
          start: request.start,
          duration: request.duration,
        },
      }),
      invalidatesTags: ['RoomSessionById'],
    }),
    overrideMeetingConsent: builder.mutation<void, OverrideMeetingConsentRequest>({
      query: (request) => ({
        url: `accounts/${request.accountId}/rooms/${request.roomId}/sessions/${request.sessionId}/consent`,
        method: 'POST',
        body: {
          reason: request.reason,
        },
      }),
      invalidatesTags: ['RoomSessionById'],
    }),
    importSession: builder.mutation<RoomSession, ImportSessionRequest>({
      query: (request) => ({
        url: `accounts/${request.accountId}/sessions/import`,
        method: 'POST',
        body: request.body,
      }),
      transformResponse: (response: BaseResponse<RoomSession>) => {
        return response.data
      },
    }),
    getSessionsByIds: builder.query<RoomSession[], GetSessionsByIdsRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/sessions/batch-get`,
        method: 'POST',
        body: request.body,
      }),
      transformResponse: (response: BaseResponse<RoomSession[]>) => response.data,
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useUpdateSessionConfigMutation,
  useUpdateSessionMetadataMutation,
  useJoinAdhocMeetingMutation,
  useGetUserActiveSessionsQuery,
  useStartSessionMutation,
  useFinishSessionMutation,
  useListFilesQuery,
  useLazyListFilesQuery,
  useUploadSessionFileRequestMutation,
  useUpdateFileMutation,
  useLazyGetRoomQuery,
  useGetRoomSessionQuery,
  useUpdateRoomMutation,
  useLazyGetRoomSessionQuery,
  useGetUserAllocationQuery,
  useUpdateUserAllocationMutation,
  useGetUserAllocationContextUsersQuery,
  useGetManualParticipantsQuery,
  useDeleteManualParticipantMutation,
  useAddAllocationContextUsersMutation,
  useRemoveAllocationContextUserMutation,
  useTriggerEventMutation,
  useListAiResultsQuery,
  useLazyListAiResultsQuery,
  useListAIFeaturesQuery,
  useLazyListAIFeaturesQuery,
  useListRoomEventsQuery,
  useLazyListRoomEventsQuery,
  useDeleteSessionMutation,
  useEndSessionMutation,
  useChangeSourceMutation,
  useChangePrivacyMutation,
  useLazyGetTranscriptionFileLocationQuery,
  useTrimMeetingMutation,
  useOverrideMeetingConsentMutation,
  useImportSessionMutation,
  useUpsertManualParticipantMutation,
  useGetSessionsByIdsQuery,
  useLazyGetSessionsByIdsQuery,
} = mediaApi
